import { cloneDeep, forEach, set, get } from 'lodash';
import { Identifiable, Modifiable, Schedulable } from '../../utils';
import { AnyPinEntry } from './PinEntry';
import { TeacherEntryUpdateData } from './TeacherEntryUpdateData';

/**
 * This object contains all teacher-entries adjustments / values for an assignment (eg. WordCloud locations and settings)
 */
export interface TeacherEntry extends Identifiable, Modifiable, Schedulable {
  userId: string;
  createdAt: number;
  assignmentId: string;
  entries: { [pinId: string]: TeacherEntryPin };
}

export interface TeacherEntryPin {
  wordCloud?: TeacherEntryPinWordCloud;
}

export interface TeacherEntryPinWordCloud {
  // note that the index-word in the hashtable is a cleaned version of the word with only lowercase, alphanum characters
  [cleanedWord: string]: {
    word: string;
    x: number;
    y: number;
    showWord: boolean;
    hidden?: boolean;
  };
}

export namespace TeacherEntry {
  export function createId(assignmentId: string): string {
    return `${assignmentId}`;
  }

  export function create(assignmentId: string, userId: string): TeacherEntry {
    const now = Date.now();

    return {
      _id: createId(assignmentId),
      createdAt: now,
      modifiedAt: now,
      assignmentId,
      userId,
      entries: {},
    };
  }

  /** creates a clone of the given entry and applies the given update logic so we can use it for e.g. comparisons */
  export function update(teacherEntry: TeacherEntry, updates: TeacherEntryUpdateData): TeacherEntry {
    const cloned = cloneDeep(teacherEntry);
    const updateFields = TeacherEntryUpdateData.updateFields(updates);
    forEach(updateFields, (value, field) => set(cloned, field, value));
    return cloned;
  }

  export function entryForPin<T extends AnyPinEntry = AnyPinEntry>(
    entry: TeacherEntry | undefined,
    pinId: string | undefined
  ): T | undefined {
    return pinId ? (get(entry && entry.entries, pinId) as T) : undefined;
  }
}
